/* ============================================================
   Home Wellbeinn — uxkid-home-athletes
   Contenido real (22/9): 9 cards de video/foto. Los videos autoplayean
   mudos en loop al entrar en viewport (IntersectionObserver). Si el
   visitante prefiere movimiento reducido, o el navegador no soporta
   IntersectionObserver, se usa el comportamiento anterior: el video queda
   inerte hasta que se toca el botón de play, y entonces se reproduce con
   controles y sonido.

   Tope de reproducción en modo autoplay (pedido de Manuel, 22/9): los
   videos reales duran 1-2 minutos, pero en un bento solo interesan los
   primeros segundos en loop (no tiene sentido autoplayear el video entero
   mudo de fondo). Se corta con un listener de "timeupdate" por video (sin
   timers sueltos: no hay que limpiar intervalos al pausar/salir de
   viewport) que lo manda de vuelta a 0 al llegar a `loop_seconds` segundos
   (setting de la sección, leído del atributo `data-uxkid-loop-seconds` del
   <section>). No aplica al fallback de tap-a-reproducir: ahí el video se
   ve completo, con sonido y controles.

   Vanilla, sin dependencias. Idempotente: se puede re-inicializar cuando el
   editor de temas vuelve a cargar la sección (shopify:section:load).
   (La card de testimonio no tiene JS: el Figma no trae botón de expandir.)
   ============================================================ */
(function () {
  'use strict';

  var DEFAULT_LOOP_SECONDS = 10;

  /* Decide si corresponde el modo autoplay (mudo, en loop, sin controles) o
     el modo fallback de tap-a-reproducir (con sonido y controles).
     Input: ninguno. Output: boolean — true si hay que usar autoplay. */
  function supportsAutoplayMode() {
    try {
      var prefersReducedMotion = window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (prefersReducedMotion) return false;
    } catch (error) {
      // Si matchMedia falla por lo que sea, no bloqueamos el autoplay por eso.
    }
    return typeof window.IntersectionObserver !== 'undefined';
  }

  /* Monta el <video> de una card (clona el <template>) si todavía no está montado.
     Input: card (.uxkid-athletes__media). Output: el elemento <video> montado, o
     null si la card no tiene template válido (se avisa por consola para debug). */
  function mountVideo(card) {
    var media = card.querySelector('[data-uxkid-athletes-media]');
    var template = card.querySelector('template[data-uxkid-athletes-video]');
    if (!media || !template) return null;

    var existing = media.querySelector('video');
    if (existing) return existing;

    try {
      var fragment = template.content.cloneNode(true);
      var video = fragment.querySelector('video');
      if (!video) {
        console.warn('[uxkid-home-athletes] El bloque no tiene un <video> válido; revisá el setting "Video" en el editor.');
        return null;
      }
      media.appendChild(fragment);
      return media.querySelector('video');
    } catch (error) {
      console.warn('[uxkid-home-athletes] No se pudo montar el video:', error);
      return null;
    }
  }

  /* Inicializa el fallback de tap-a-reproducir de una card (prefers-reduced-motion
     o sin IntersectionObserver): al tocar el play, monta el video con sonido y
     controles y lo reproduce.
     Input: card (.uxkid-athletes__media) con [data-uxkid-athletes-play].
     Output: nada; maneja sus propios errores por consola. */
  function initTapToPlay(card) {
    var button = card.querySelector('[data-uxkid-athletes-play]');
    if (!button || button.dataset.uxkidReady) return;
    button.dataset.uxkidReady = '1';

    button.addEventListener('click', function () {
      var video = mountVideo(card);
      if (!video) return;
      try {
        // El <template> trae el video mudo/loop por defecto (modo autoplay);
        // en este fallback el video se ve con sonido y controles nativos.
        video.muted = false;
        video.loop = false;
        video.controls = true;
        card.classList.add('is-playing');
        var playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') {
          // Si el navegador bloquea el autoplay con sonido, quedan los controles visibles.
          playPromise.catch(function () {});
        }
      } catch (error) {
        console.warn('[uxkid-home-athletes] No se pudo reproducir el video:', error);
      }
    });
  }

  /* Lee `loop_seconds` desde el atributo `data-uxkid-loop-seconds` de la sección.
     Input: section (elemento [data-uxkid-athletes]). Output: number > 0 (el
     tiempo en segundos al que el video autoplay vuelve a 0); si el atributo
     falta o no es un número válido, se usa DEFAULT_LOOP_SECONDS. */
  function getLoopSeconds(section) {
    var raw = section.getAttribute('data-uxkid-loop-seconds');
    var seconds = parseFloat(raw);
    if (!isFinite(seconds) || seconds <= 0) return DEFAULT_LOOP_SECONDS;
    return seconds;
  }

  /* Corta la reproducción de un video autoplay a los primeros `loopSeconds`
     segundos: al llegar ahí, lo manda de vuelta a 0 (sigue en loop dentro de
     ese rango). Usa "timeupdate" en vez de un timer propio para no tener que
     limpiar intervalos cuando el video se pausa o la card sale de viewport.
     Input: video (<video> montado), loopSeconds (number). Output: nada;
     idempotente (no duplica el listener si ya se aplicó a este video). */
  function applyLoopCap(video, loopSeconds) {
    if (video.dataset.uxkidLoopCapped) return;
    video.dataset.uxkidLoopCapped = '1';
    video.addEventListener('timeupdate', function () {
      if (video.currentTime >= loopSeconds) {
        video.currentTime = 0;
      }
    });
  }

  /* Inicializa el autoplay mudo/en loop de una card cuando entra en viewport.
     Input: card (.uxkid-athletes__media) con video real, loopSeconds (number,
     tope de reproducción de la sección). Output: nada; guarda el
     IntersectionObserver en la card para poder desconectarlo si hace falta. */
  function initAutoplay(card, loopSeconds) {
    if (card.dataset.uxkidAutoplayReady) return;
    card.dataset.uxkidAutoplayReady = '1';
    card.classList.add('is-autoplay');

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = card.querySelector('video');
        if (entry.isIntersecting) {
          if (!video) video = mountVideo(card);
          if (!video) return;
          applyLoopCap(video, loopSeconds);
          try {
            // iOS solo permite autoplay si "muted" está seteado como propiedad,
            // no alcanza con el atributo que ya trae el video_tag.
            video.muted = true;
            var playPromise = video.play();
            if (playPromise && typeof playPromise.catch === 'function') {
              playPromise.catch(function () {});
            }
          } catch (error) {
            console.warn('[uxkid-home-athletes] No se pudo autoplayear el video:', error);
          }
        } else if (video) {
          try {
            video.pause();
          } catch (error) {
            // Pausar nunca debería tirar error, pero no vale la pena romper el resto por esto.
          }
        }
      });
    }, { rootMargin: '200px 0px' });

    observer.observe(card);
    card.uxkidAthletesObserver = observer;
  }

  /* Recorre todas las secciones de comunidad del documento e inicializa sus
     cards de video, en modo autoplay o en modo tap-a-reproducir según el
     dispositivo/preferencias del visitante.
     Input: ninguno. Output: nada. */
  function init() {
    var sections = document.querySelectorAll('[data-uxkid-athletes]');
    var autoplay = supportsAutoplayMode();
    Array.prototype.forEach.call(sections, function (section) {
      var cards = section.querySelectorAll('.uxkid-athletes__media--video');
      var loopSeconds = getLoopSeconds(section);
      Array.prototype.forEach.call(cards, function (card) {
        if (autoplay) {
          initAutoplay(card, loopSeconds);
        } else {
          initTapToPlay(card);
        }
      });
    });
  }

  /* Desconecta los IntersectionObserver activos antes de re-inicializar (el
     editor de temas puede volver a renderizar la sección con nodos nuevos).
     Input: ninguno. Output: nada. */
  function teardown() {
    var cards = document.querySelectorAll('[data-uxkid-athletes] .uxkid-athletes__media--video[data-uxkid-autoplay-ready]');
    Array.prototype.forEach.call(cards, function (card) {
      if (card.uxkidAthletesObserver) {
        try {
          card.uxkidAthletesObserver.disconnect();
        } catch (error) {
          // No pasa nada si ya estaba desconectado.
        }
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // El editor de temas re-renderiza la sección al editar settings: desconectar
  // observers viejos (apuntan a nodos que ya no están) y re-inicializar sobre los nuevos.
  document.addEventListener('shopify:section:load', function () {
    teardown();
    init();
  });
})();
